import { useState, useEffect, useCallback, useMemo } from 'react';
import EstadisticasOrdenService from '../services/EstadisticasOrdenService';

/**
 * Hook personalizado para manejar las estadísticas de una orden
 * @param {string} ordenId - ID de la orden
 * @param {boolean} enabled - Si el hook debe estar activo
 * @returns {Object} Estado y funciones para manejar estadísticas
 */
export const useEstadisticasOrden = (ordenId, enabled = true) => {
  const [estadisticas, setEstadisticas] = useState({
    procesos: [],
    resumen: null,
    loading: false,
    error: null
  });

  // Usar useMemo para evitar recrear el servicio en cada render
  const estadisticasService = useMemo(() => new EstadisticasOrdenService(), []);

  /**
   * Carga las estadísticas de la orden
   */
  const cargarEstadisticas = useCallback(async () => {
    if (!ordenId || !enabled) {
      return;
    }

    setEstadisticas(prev => ({ ...prev, loading: true, error: null }));

    try {
      const resultado = await estadisticasService.obtenerEstadisticasOrden(ordenId);
      
      setEstadisticas({
        procesos: resultado?.procesos || [],
        resumen: resultado?.resumen || null,
        loading: false,
        error: resultado?.error || null
      });
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
      setEstadisticas(prev => ({
        ...prev,
        loading: false,
        error: `Error al cargar estadísticas: ${error.message}`
      }));
    }
  }, [ordenId, enabled, estadisticasService]);

  /**
   * Recarga las estadísticas
   */
  const recargarEstadisticas = useCallback(() => {
    cargarEstadisticas();
  }, [cargarEstadisticas]);

  /**
   * Limpia las estadísticas
   */
  const limpiarEstadisticas = useCallback(() => {
    setEstadisticas({
      procesos: [],
      resumen: null,
      loading: false,
      error: null
    });
  }, []);

  // Cargar estadísticas cuando cambie el ordenId o enabled
  useEffect(() => {
    if (ordenId && enabled) {
      cargarEstadisticas();
    } else if (!enabled) {
      limpiarEstadisticas();
    }
  }, [ordenId, enabled, cargarEstadisticas, limpiarEstadisticas]);

  return {
    // Estado
    procesos: estadisticas.procesos,
    resumen: estadisticas.resumen,
    loading: estadisticas.loading,
    error: estadisticas.error,
    
    // Funciones
    cargarEstadisticas,
    recargarEstadisticas,
    limpiarEstadisticas
  };
};

export default useEstadisticasOrden;
